import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Send as SendIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import emailSettingsApi from '@crema/services/apis/emailSettings';

const EmailSettingsUI = ({
  settings,
  recipients,
  schedulerStatus,
  onSuccess,
  onError,
}) => {
  const [testingConnection, setTestingConnection] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [triggeringReports, setTriggeringReports] = useState(false);
  const isMountedRef = useRef(true);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleTestConnection = async () => {
    if (!settings) {
      onError('No SMTP settings configured');
      return;
    }

    try {
      if (isMountedRef.current) {
        setTestingConnection(true);
      }
      const response = await emailSettingsApi.testSmtpConnection(settings);

      if (isMountedRef.current) {
        if (response.data.status === 200 && response.data.data.isValid) {
          onSuccess('SMTP connection test successful');
        } else {
          onError('SMTP connection test failed');
        }
      }
    } catch (error) {
      console.error('Error testing SMTP connection:', error);
      if (isMountedRef.current) {
        onError('Failed to test SMTP connection');
      }
    } finally {
      if (isMountedRef.current) {
        setTestingConnection(false);
      }
    }
  };

  const handleSendTestEmail = async () => {
    if (!settings || recipients.length === 0) {
      onError('No SMTP settings or recipients configured');
      return;
    }

    try {
      if (isMountedRef.current) {
        setSendingTest(true);
      }
      const testRecipient =
        recipients.find((r) => r.is_active) || recipients[0];

      const response = await emailSettingsApi.sendTestEmail({
        recipient_email: testRecipient.email,
        subject: 'Test Email - Daily Reports System',
        message:
          'This is a test email to verify your email configuration is working correctly.',
      });

      if (isMountedRef.current) {
        if (response.data.status === 200 && response.data.data.emailSent) {
          onSuccess(`Test email sent successfully to ${testRecipient.email}`);
        } else {
          onError('Failed to send test email');
        }
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      if (isMountedRef.current) {
        onError('Failed to send test email');
      }
    } finally {
      if (isMountedRef.current) {
        setSendingTest(false);
      }
    }
  };

  const handleTriggerDailyReports = async () => {
    try {
      if (isMountedRef.current) {
        setTriggeringReports(true);
      }
      const response = await emailSettingsApi.triggerDailyReports();

      if (isMountedRef.current) {
        if (response.data.status === 200) {
          onSuccess('Daily reports triggered successfully');
        } else {
          onError(response.data.message || 'Failed to trigger daily reports');
        }
      }
    } catch (error) {
      console.error('Error triggering daily reports:', error);
      if (isMountedRef.current) {
        onError('Failed to trigger daily reports');
      }
    } finally {
      if (isMountedRef.current) {
        setTriggeringReports(false);
      }
    }
  };

  const activeRecipients = recipients.filter((r) => r.is_active);
  const inactiveRecipients = recipients.filter((r) => !r.is_active);

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Email Settings Overview
      </Typography>

      <Grid container spacing={3}>
        {/* SMTP Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                SMTP Configuration
              </Typography>

              {settings ? (
                <Box>
                  <Box display='flex' alignItems='center' mb={2}>
                    <CheckCircleIcon color='success' sx={{mr: 1}} />
                    <Typography variant='body1'>SMTP configured</Typography>
                  </Box>

                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary='Host'
                        secondary={settings.email_host}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary='Port'
                        secondary={settings.email_port}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary='From Email'
                        secondary={settings.from_email}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary='From Name'
                        secondary={settings.from_name}
                      />
                    </ListItem>
                  </List>

                  <Button
                    variant='outlined'
                    startIcon={<SendIcon />}
                    onClick={handleTestConnection}
                    disabled={testingConnection}
                    sx={{mt: 2}}
                  >
                    {testingConnection ? 'Testing...' : 'Test Connection'}
                  </Button>
                </Box>
              ) : (
                <Alert severity='warning'>
                  No SMTP configuration found. Please configure SMTP settings
                  first.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recipients Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Recipients
              </Typography>

              <Box display='flex' alignItems='center' mb={2}>
                <CheckCircleIcon color='success' sx={{mr: 1}} />
                <Typography variant='body1'>
                  {activeRecipients.length} active recipients
                </Typography>
              </Box>

              {inactiveRecipients.length > 0 && (
                <Box display='flex' alignItems='center' mb={2}>
                  <ErrorIcon color='warning' sx={{mr: 1}} />
                  <Typography variant='body1'>
                    {inactiveRecipients.length} inactive recipients
                  </Typography>
                </Box>
              )}

              <List dense>
                {activeRecipients.slice(0, 3).map((recipient) => (
                  <ListItem key={recipient.id}>
                    <ListItemText
                      primary={recipient.name}
                      secondary={recipient.email}
                    />
                  </ListItem>
                ))}
                {activeRecipients.length > 3 && (
                  <ListItem>
                    <ListItemText
                      secondary={`... and ${activeRecipients.length - 3} more`}
                    />
                  </ListItem>
                )}
              </List>

              <Button
                variant='outlined'
                startIcon={<SendIcon />}
                onClick={handleSendTestEmail}
                disabled={
                  sendingTest || !settings || activeRecipients.length === 0
                }
                sx={{mt: 2}}
              >
                {sendingTest ? 'Sending...' : 'Send Test Email'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Scheduler Status */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Scheduler Status
              </Typography>

              {schedulerStatus ? (
                <Box>
                  <Box display='flex' alignItems='center' mb={2}>
                    {schedulerStatus.isScheduled ? (
                      <PlayArrowIcon color='success' sx={{mr: 1}} />
                    ) : (
                      <StopIcon color='disabled' sx={{mr: 1}} />
                    )}
                    <Typography variant='body1'>
                      {schedulerStatus.isScheduled ? 'Currently Running' : 'Idle'}
                    </Typography>
                  </Box>

                  <Typography
                    variant='body2'
                    color='text.secondary'
                    gutterBottom
                  >
                    Scheduler Details:
                  </Typography>

                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ScheduleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Daily Reports"
                        secondary={`Schedule: ${schedulerStatus.config?.cron || 'Not configured'} | Timezone: ${schedulerStatus.config?.timezone || 'UTC'}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PlayArrowIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Next Run"
                        secondary={schedulerStatus.nextRun || 'Not scheduled'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <StopIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Current Time"
                        secondary={schedulerStatus.currentTime || 'Unknown'}
                      />
                    </ListItem>
                  </List>

                  <Divider sx={{my: 2}} />

                  <Button
                    variant='contained'
                    startIcon={<PlayArrowIcon />}
                    onClick={handleTriggerDailyReports}
                    disabled={triggeringReports}
                    sx={{mt: 1}}
                  >
                    {triggeringReports
                      ? 'Triggering...'
                      : 'Trigger Daily Reports Now'}
                  </Button>
                </Box>
              ) : (
                <Alert severity='warning'>Scheduler status unavailable</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

EmailSettingsUI.propTypes = {
  settings: PropTypes.object,
  recipients: PropTypes.array,
  schedulerStatus: PropTypes.object,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

export default EmailSettingsUI;
